import { useEffect, useState } from 'react';
import parentsCard1 from './assets/news/news3.jpg';
import parentsCard2 from './assets/news/news4.jpg';
import parentsCard3 from './assets/news/news7.jpg';

function PaLCDetailPage({ palcId, onNavigate }) {
  const [activeImageSlide, setActiveImageSlide] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [palcId]);

  const palcData = {
    'palc-support': {
      title: 'Family Support Circles',
      subtitle: 'Building Connections & Peer Support',
      description: 'Monthly gatherings designed to connect families, share insights, and build a supportive parent network that strengthens our school community.',
      color: '#7c3aed',
      colorLight: '#ede9fe',
      icon: 'fa-people-group',
      images: [parentsCard1, parentsCard2, parentsCard3],
      date: 'Monthly',
      frequency: 'Every 2nd Saturday',
      features: [
        {
          icon: 'fa-users',
          title: 'Peer Connection',
          detail: 'Meet other parents, share experiences, and build meaningful relationships with families in our community.'
        },
        {
          icon: 'fa-comments',
          title: 'Open Discussions',
          detail: 'Facilitated conversations on parenting challenges, child development, and school life experiences.'
        },
        {
          icon: 'fa-lightbulb',
          title: 'Shared Insights',
          detail: 'Learn from experienced parents and educators about effective parenting strategies and support systems.'
        },
        {
          icon: 'fa-heart',
          title: 'Emotional Support',
          detail: 'A safe, welcoming space where parents can share concerns and receive encouragement from peers.'
        }
      ],
      highlights: [
        '50+ families participate in monthly gatherings',
        'Expert facilitators guide meaningful discussions',
        'Refreshments and childcare provided',
        'Flexible scheduling for working parents'
      ],
      content: 'Family Support Circles are the heart of our Parents Learning Community. These monthly gatherings create a safe, welcoming space where parents can connect with others, share their experiences, and receive support from peers who understand the joys and challenges of parenting. Our expert facilitators guide discussions on topics ranging from managing school transitions to supporting your child\'s learning at home. Whether you\'re a new parent or a seasoned one, these circles offer valuable connections and practical insights that strengthen both your family and our school community.'
    },
    'palc-workshops': {
      title: 'Learning Workshops',
      subtitle: 'Empowering Parents Through Education',
      description: 'Interactive sessions on learning strategies, child wellbeing, and student development at home. Designed to equip parents with practical tools and knowledge.',
      color: '#06b6d4',
      colorLight: '#cffafe',
      icon: 'fa-book-open',
      images: [parentsCard2, parentsCard3, parentsCard1],
      date: 'Quarterly',
      frequency: 'Every 3 months',
      features: [
        {
          icon: 'fa-brain',
          title: 'Learning Strategies',
          detail: 'Discover effective techniques to support your child\'s academic growth and foster a love of learning at home.'
        },
        {
          icon: 'fa-heart-pulse',
          title: 'Child Wellbeing',
          detail: 'Learn about emotional intelligence, stress management, and creating a healthy home environment for your child.'
        },
        {
          icon: 'fa-chart-line',
          title: 'Progress Tracking',
          detail: 'Understand how to monitor your child\'s development and communicate effectively with teachers about progress.'
        },
        {
          icon: 'fa-graduation-cap',
          title: 'Educational Excellence',
          detail: 'Expert-led sessions on curriculum, learning goals, and how to support your child\'s academic journey.'
        }
      ],
      highlights: [
        '8+ workshops offered annually',
        'Expert speakers from education and psychology fields',
        'Practical takeaways and resource materials',
        'Q&A sessions with educators and specialists'
      ],
      content: 'Our Learning Workshops are designed to empower parents with the knowledge and tools needed to support their children\'s academic and personal development. Led by experienced educators, psychologists, and specialists, these interactive sessions cover topics such as effective study habits, managing test anxiety, supporting different learning styles, and fostering emotional intelligence. Each workshop combines expert insights with practical strategies that parents can immediately apply at home. Participants receive comprehensive resource materials and have the opportunity to ask questions and share experiences with other parents facing similar challenges.'
    },
    'palc-community': {
      title: 'Community Collaboration',
      subtitle: 'Parents & School Working Together',
      description: 'Joint initiatives that connect parents with school programs, events, and service projects. Creating meaningful partnerships that benefit our entire community.',
      color: '#f59e0b',
      colorLight: '#fef3c7',
      icon: 'fa-handshake',
      images: [parentsCard3, parentsCard1, parentsCard2],
      date: 'Ongoing',
      frequency: 'Throughout the year',
      features: [
        {
          icon: 'fa-calendar-check',
          title: 'Event Coordination',
          detail: 'Parents volunteer to organize and lead school events, celebrations, and community gatherings.'
        },
        {
          icon: 'fa-hands-helping',
          title: 'Service Projects',
          detail: 'Participate in community service initiatives that make a positive impact on our local neighborhood.'
        },
        {
          icon: 'fa-school',
          title: 'School Programs',
          detail: 'Support classroom activities, field trips, and enrichment programs that enhance student learning.'
        },
        {
          icon: 'fa-star',
          title: 'Leadership Roles',
          detail: 'Take on leadership positions in parent committees and contribute to school decision-making.'
        }
      ],
      highlights: [
        '200+ parent volunteers annually',
        '15+ community service projects per year',
        'Parent-led committees and initiatives',
        'Direct impact on school culture and community'
      ],
      content: 'Community Collaboration brings parents and school together in meaningful ways. Through volunteer opportunities, service projects, and collaborative initiatives, parents become active partners in creating a vibrant school community. Whether organizing school events, leading service projects, or supporting classroom activities, parent volunteers strengthen our school culture and demonstrate to students the value of community engagement. These collaborations create a sense of belonging and shared purpose, making our school a true community where families feel invested and valued.'
    }
  };

  const palc = palcData[palcId] || palcData['palc-support'];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveImageSlide((current) => (current + 1) % palc.images.length);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [palc.images.length]);

  return (
    <section className="palc-detail-page">
      {/* Hero Section */}
      <section className="palc-detail-hero" style={{ backgroundColor: palc.color }}>
        <div className="palc-detail-hero-pattern" />
        <div className="palc-detail-hero-image">
          <div className="palc-detail-hero-media" aria-hidden="true">
            {palc.images.map((image, index) => (
              <div
                key={`${palcId}-${index}`}
                className={`palc-detail-slide ${index === activeImageSlide ? 'active' : ''}`}
              >
                <img src={image} alt={palc.title} />
              </div>
            ))}
          </div>
          <div className="palc-detail-hero-overlay" aria-hidden="true" />
        </div>
        <div className="palc-detail-hero-content">
          <button
            className="palc-detail-back-btn"
            onClick={() => onNavigate && onNavigate('parents')}
            aria-label="Back to parents"
          >
            <i className="fas fa-arrow-left" /> Back
          </button>
          <div className="palc-detail-hero-icon">
            <i className={`fas ${palc.icon}`} />
          </div>
          <h1 className="palc-detail-hero-title">{palc.title}</h1>
          <p className="palc-detail-hero-subtitle">{palc.subtitle}</p>
          <div className="palc-detail-meta">
            <span className="palc-detail-frequency"><i className="fas fa-clock" /> {palc.frequency}</span>
            <span className="palc-detail-schedule"><i className="fas fa-calendar" /> {palc.date}</span>
          </div>
        </div>
        <div className="palc-detail-hero-dots">
          {palc.images.map((_, index) => (
            <button
              key={`dot-${index}`}
              type="button"
              className={`palc-detail-dot ${index === activeImageSlide ? 'active' : ''}`}
              onClick={() => setActiveImageSlide(index)}
              aria-label={`Show slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Main Content */}
      <section className="palc-detail-main">
        {/* About Section */}
        <div className="palc-detail-section">
          <div className="palc-detail-section-header">
            <h2>About This Program</h2>
            <div className="palc-detail-section-line" style={{ backgroundColor: palc.color }} />
          </div>
          <p className="palc-detail-description">{palc.description}</p>
          <div className="palc-detail-highlight-box" style={{ backgroundColor: palc.colorLight, borderLeftColor: palc.color }}>
            <i className={`fas ${palc.icon}`} style={{ color: palc.color }} />
            <p>{palc.content}</p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="palc-detail-section">
          <div className="palc-detail-section-header">
            <h2>What You'll Experience</h2>
            <div className="palc-detail-section-line" style={{ backgroundColor: palc.color }} />
          </div>
          <div className="palc-features-grid">
            {palc.features.map((feature, index) => (
              <div key={`feature-${index}`} className="palc-feature-card" style={{ borderTopColor: palc.color }}>
                <div 
                  className="palc-feature-icon" 
                  style={{ 
                    '--feature-color': palc.color,
                    '--feature-color-dark': palc.color + 'cc'
                  }}
                >
                  <i className={`fas ${feature.icon}`} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Highlights */}
        <div className="palc-detail-section">
          <div className="palc-detail-section-header">
            <h2>Program Highlights</h2>
            <div className="palc-detail-section-line" style={{ backgroundColor: palc.color }} />
          </div>
          <div className="palc-highlights-grid">
            {palc.highlights.map((highlight, index) => (
              <div key={`highlight-${index}`} className="palc-highlight-card" style={{ backgroundColor: palc.colorLight, borderLeftColor: palc.color }}>
                <div className="palc-highlight-icon" style={{ color: palc.color }}>
                  <i className="fas fa-check-circle" />
                </div>
                <p>{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="palc-detail-cta" 
        style={{ 
          '--cta-color': palc.color,
          '--cta-color-dark': palc.color + 'cc'
        }}
      >
        <div className="palc-detail-cta-content">
          <h2>Join Our Community</h2>
          <p>Become part of a supportive network of parents committed to student success and community growth.</p>
          <button className="palc-detail-cta-btn" onClick={() => onNavigate && onNavigate('contact')}>
            Get Involved <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>
    </section>
  );
}

export default PaLCDetailPage;
